import React, { Fragment, FunctionComponent } from "react";
import styles from "./MobileMenu.module.scss";
import MobileMenuProps from "./MobileMenuProps.interface";

import { CLOSE_ALL_OVERLAYS, OPEN_MOBILE_MENU } from "layout/redux/overlay.actions";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import Anchor from "components/Anchor/Anchor";
import MenuLink from "blocks/models/menu-link.interface";
import Dictionary from "components/Dictionary/Dictionary";
import getCulturesList from "../CountriesOverlay/helpers/getCulturesList";
import useCategoriesMapping from "layout/Sidemenu/hooks/useCategoriesMapping";
import combineClasses from "helpers/combine-classes";
import relativeUrl from "helpers/relativeUrl";

const MobileMenu: FunctionComponent<MobileMenuProps> = (props) => {
    const dispatch = useDispatch();
    const settings = useSelector((state: any) => state.settings.content);
    const categories: any[] = useCategoriesMapping();

    const handleClick = () => {
        dispatch({
            type: CLOSE_ALL_OVERLAYS,
        });
    };

    const goBack = () => {
        dispatch({
            type: OPEN_MOBILE_MENU,
            content: undefined,
        });
    };

    const goForward = (event: any, item?: MenuLink | string) => {
        event?.preventDefault();

        dispatch({
            type: OPEN_MOBILE_MENU,
            content: item,
        });
    };

    const cultures = getCulturesList(props.cultures, props.translations);

    const isActive = (lang: string, styles: any) => {
        if (lang === props.culture) {
            return styles.is_active;
        }

        return undefined;
    };

    return (
        <Fragment>
            {/* Lock the scrollbar for the rest of the page on mobile, while drawer is open */}
            {/* TODO: Remember the scroll position and restore it when toggling locked state */}
            <Helmet bodyAttributes={{ class: "is-locked" }} />

            <nav className={styles.wrapper}>
                <div className={styles.inner}>
                    <div className={styles.content}>
                        {!props.menu ? (
                            <Fragment>
                                <nav className={styles.navigation}>
                                    {/* 1. level: Products */}
                                    {categories && categories.length > 0 ? (
                                        <div
                                            key="product_item"
                                            className={styles.menu_item}
                                        >
                                            <button
                                                className={styles.menu_button}
                                                onClick={(event) => goForward(event, "PRODUCTS")}
                                            >
                                                <Dictionary name="HEADER.PRODUCTS" />
                                            </button>
                                        </div>
                                    ) : null}

                                    {/* 1. level: Content */}
                                    {settings?.menu &&
                                        settings?.menu.map((item: any) => {
                                            return (
                                                <div
                                                    key={item.contentUdi}
                                                    className={styles.menu_item}
                                                >
                                                    {/* Has Children */}
                                                    {item.content.subMenu && item.content.subMenu.length > 0 ? (
                                                        <button
                                                            className={styles.menu_button}
                                                            onClick={(event) => goForward(event, item)}
                                                        >
                                                            {item.content.label}
                                                        </button>
                                                    ) : null}

                                                    {/* No Children */}
                                                    {!item.content.subMenu || item.content.subMenu.length === 0 ? (
                                                        <Anchor
                                                            className={styles.menu_link}
                                                            to={item.content.link}
                                                            onClick={handleClick}
                                                        >
                                                            {item.content.label}
                                                        </Anchor>
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                </nav>

                                {/* 1. level: Cultures */}
                                {cultures && cultures.length > 1 ? (
                                    <button
                                        className={styles.culture_button}
                                        onClick={(event) => goForward(event, "CULTURES")}
                                    >
                                        {cultures.find((x) => x.lang === props.culture)?.label}
                                    </button>
                                ) : null}
                            </Fragment>
                        ) : null}

                        {/* 2. level: Content */}
                        {props.menu && (props.menu as any) !== "PRODUCTS" && (props.menu as any) !== "CULTURES" ? (
                            <Fragment>
                                <button
                                    className={styles.back_button}
                                    onClick={() => goBack()}
                                >
                                    {props.menu.content.label}
                                </button>

                                <nav className={styles.sub_navigation}>
                                    <Anchor
                                        className={styles.sub_link}
                                        to={props.menu.content.link}
                                        onClick={handleClick}
                                    >
                                        {props.menu.content.label}
                                    </Anchor>

                                    {props.menu?.content.subMenu &&
                                        props.menu.content.subMenu.map((secondItem) => {
                                            return (
                                                <Anchor
                                                    key={secondItem.contentUdi}
                                                    className={styles.sub_link}
                                                    to={secondItem?.content?.link}
                                                    onClick={handleClick}
                                                >
                                                    {secondItem?.content?.label}
                                                </Anchor>
                                            );
                                        })}
                                </nav>
                            </Fragment>
                        ) : null}

                        {/* 2. level: Products */}
                        {(props.menu as any) === "PRODUCTS" ? (
                            <Fragment>
                                <button
                                    className={styles.back_button}
                                    onClick={() => goBack()}
                                >
                                    <Dictionary name="HEADER.PRODUCTS" />
                                </button>

                                <nav className={styles.sub_navigation}>
                                    {categories?.map((mainCategory: any, index: number) => {
                                        return (
                                            <Fragment key={`m_${index}`}>
                                                {mainCategory.subCategories && mainCategory.subCategories.length > 0 ? (
                                                    <Fragment>
                                                        <strong className={styles.sub_headline}>{mainCategory.category.primaryTitle}</strong>

                                                        {mainCategory.subCategories?.map((subCategory: any, subIndex: number) => {
                                                            return (
                                                                <Anchor
                                                                    key={`sm_${subIndex}`}
                                                                    className={styles.sub_product_link}
                                                                    to={subCategory.url}
                                                                    onClick={handleClick}
                                                                >
                                                                    {subCategory.category.primaryTitle}
                                                                </Anchor>
                                                            );
                                                        })}
                                                    </Fragment>
                                                ) : null}
                                            </Fragment>
                                        );
                                    })}
                                </nav>
                            </Fragment>
                        ) : null}

                        {/* 2. level: Cultures */}
                        {(props.menu as any) === "CULTURES" ? (
                            <Fragment>
                                <button
                                    className={styles.back_button}
                                    onClick={() => goBack()}
                                >
                                    <Dictionary name="NAVIGATION.BACK" />
                                </button>

                                <nav className={styles.sub_navigation}>
                                    {cultures &&
                                        cultures.map((culture: any, index: number) => {
                                            return (
                                                <a
                                                    key={index}
                                                    className={combineClasses([styles.sub_link, isActive(culture.lang, styles)])}
                                                    href={relativeUrl(culture.link)}
                                                    onClick={handleClick}
                                                >
                                                    {culture.label}
                                                </a>
                                            );
                                        })}
                                </nav>
                            </Fragment>
                        ) : null}
                    </div>
                </div>
            </nav>
        </Fragment>
    );
};

export default MobileMenu;
